import React, {
  SyntheticEvent,
  useCallback,
  useId,
  useMemo,
  useState,
} from 'react';
import { FieldChoice, useIsAdmin, useSession } from 'queries';
import { Chip, MenuItem, MenuWindow, SquareButton } from '@app/core';
import { createUseStyles } from 'react-jss';
import { Colors, Theme } from '@app/theme';
import classNames from 'classnames';

interface Props {
  onChange: (index: number, choice: FieldChoice) => any;
  choice: FieldChoice;
  index: number;
  sessionId?: string;
}

const useStyles = createUseStyles((theme: Theme) => ({
  iconColor: {
    color: theme.colors.purpleAccessible,
  },
}));

export const ChoiceTagButton = ({
  onChange,
  choice,
  sessionId,
  index,
}: Props) => {
  const isAdmin = useIsAdmin();
  const classes = useStyles();

  const id = useId();

  const [anchorElmnt, setAnchorElmnt] = useState<null | Element>(null);
  const { data: session } = useSession(sessionId);
  const tags = session ? session.availableTags || [] : [];

  const onClick = useCallback(
    (event: SyntheticEvent) => {
      event.stopPropagation();
      setAnchorElmnt(event.currentTarget);
    },
    [setAnchorElmnt],
  );

  const onCloseSelector = useCallback(() => {
    setAnchorElmnt(null);
  }, [setAnchorElmnt]);

  const onAddTag = useCallback(
    (tagId: string) => {
      if (choice.metadata?.tags?.includes(tagId)) {
        onChange(index, {
          ...choice,
          metadata: {
            ...choice.metadata,
            tags: choice.metadata.tags.filter((t) => t !== tagId),
          },
        });
        return;
      }

      onChange(index, {
        ...choice,
        metadata: {
          ...choice.metadata,
          tags: [...(choice.metadata?.tags || []), tagId],
        },
      });
    },
    [onChange, choice, index],
  );

  const selectedTags = choice.metadata?.tags;

  const color: Colors = useMemo(() => {
    if (!selectedTags?.length) {
      return 'purple';
    }
    const id = selectedTags[0];

    return tags.find((t) => t.id === id)?.color || 'purple';
  }, [selectedTags, tags]);

  if (!isAdmin || !tags.length) {
    return null;
  }

  return (
    <>
      <SquareButton
        color={color}
        icon={classNames('ri-price-tag-3-line', classes.iconColor)}
        onClick={onClick}
        variant={!!choice.metadata?.tags ? 'muted' : 'transparent'}
      />
      <MenuWindow
        open={!!anchorElmnt}
        onClose={onCloseSelector}
        anchorEl={anchorElmnt}
        id={id}
      >
        {tags.map((tag) => (
          <MenuItem
            value={tag.id}
            key={tag.id}
            selected={choice.metadata?.tags?.includes(tag.id)}
            onSelect={onAddTag}
          >
            <Chip
              customColor={tag.color}
              key={tag.id}
              variant="selectable"
              selected={choice.metadata?.tags?.includes(tag.id)}
            >
              {tag.name}
            </Chip>
          </MenuItem>
        ))}
      </MenuWindow>
    </>
  );
};
